import { useMemo, useState } from "react";
import {
  ArrowUpRight,
  BriefcaseBusiness,
  CheckCircle2,
  CircleAlert,
  FileSearch,
  Search,
  Scale,
  X,
} from "lucide-react";
import { Field, StatusPill } from "../components/Ui";
import { CDM_REGULATION_LIBRARY, POLICY_PACK } from "../data/cdmPolicyPack";
import { assessProject } from "../lib/assessmentEngine";
import type {
  ActionStatus,
  DutyAction,
  DutyHolder,
  EvidenceItem,
  PolicyRule,
  Project,
} from "../types/domain";

const dutyHolderLabels: Record<DutyHolder, string> = {
  client: "Client",
  designer: "Designer",
  "principal-designer": "Principal designer",
  contractor: "Contractor",
  "principal-contractor": "Principal contractor",
  all: "Project-wide",
};

const blankAction = (ruleId: string): DutyAction => ({
  id: crypto.randomUUID(),
  ruleId,
  status: "open",
  owner: "",
  dueDate: "",
  response: "",
  evidenceIds: [],
  updatedAt: new Date().toISOString(),
});

export function DutiesView({
  project,
  actions,
  evidence,
  onSaveAction,
}: {
  project: Project;
  actions: DutyAction[];
  evidence: EvidenceItem[];
  onSaveAction: (action: DutyAction) => void;
}) {
  const assessment = assessProject(project);
  const [query, setQuery] = useState("");
  const [holder, setHolder] = useState<"all" | DutyHolder>("all");
  const [draft, setDraft] = useState<{ rule: PolicyRule; action: DutyAction } | null>(null);
  const rules = useMemo(
    () =>
      assessment.triggeredRules.filter(
        (rule) =>
          (holder === "all" || rule.dutyHolder === holder) &&
          `${rule.regulation} ${rule.title} ${rule.requirement} ${rule.expectedEvidence.join(" ")}`
            .toLowerCase()
            .includes(query.toLowerCase()),
      ),
    [assessment.triggeredRules, holder, query],
  );
  const complete = assessment.triggeredRules.filter(
    (rule) => actions.find((action) => action.ruleId === rule.id)?.status === "complete",
  ).length;

  const editAction = (rule: PolicyRule) => {
    setDraft({ rule, action: actions.find((item) => item.ruleId === rule.id) ?? blankAction(rule.id) });
  };

  return (
    <>
      <section className="duty-summary">
        <article className="duty-summary-main">
          <span className="summary-icon"><Scale size={25} /></span>
          <div>
            <span className="eyebrow">Source-mapped applicability</span>
            <h2>{assessment.cdmRules.length} CDM obligations · {assessment.relatedRules.length + assessment.approvalGateways.length} related checks</h2>
            <p>
              All 39 CDM regulations are indexed. Applicable duties become owned, dated actions
              linked to evidence rather than passive checklist answers.
            </p>
          </div>
        </article>
        <div className="duty-flags">
          <div><small>Duty actions complete</small><StatusPill status={complete === assessment.triggeredRules.length ? "success" : "warning"}>{complete} / {assessment.triggeredRules.length}</StatusPill></div>
          <div><small>Notifiable project</small><StatusPill status={assessment.notifiable ? "warning" : "neutral"}>{assessment.notifiable ? "Threshold exceeded" : "Below recorded threshold"}</StatusPill></div>
        </div>
      </section>

      {assessment.warnings.length > 0 && (
        <section className="inline-warning">
          <CircleAlert size={20} />
          <div><strong>Duty map contains unresolved inputs</strong><span>{assessment.warnings.join(" ")}</span></div>
        </section>
      )}

      <section className="toolbar">
        <div className="search-box">
          <Search size={17} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search regulation, requirement or evidence" />
        </div>
        <select className="toolbar-select" value={holder} onChange={(e) => setHolder(e.target.value as "all" | DutyHolder)}>
          <option value="all">All dutyholders</option>
          {Object.entries(dutyHolderLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </select>
      </section>

      <section className="regulation-library-strip">
        <div>
          <strong>Complete instrument</strong>
          <span>{CDM_REGULATION_LIBRARY.length} regulations · 4 schedules · exact PDF page links</span>
        </div>
        <a href={`${POLICY_PACK.sourceUrl}#page=2`} target="_blank" rel="noreferrer">Open contents <ArrowUpRight size={14} /></a>
      </section>

      <section className="duty-list regulation-action-grid">
        {rules.map((rule) => {
          const action = actions.find((item) => item.ruleId === rule.id);
          return (
            <article className={`duty-card duty-action-${action?.status ?? "open"}`} key={rule.id}>
              <div className="duty-card-top">
                <span className="regulation-number">{rule.regulation}</span>
                <StatusPill
                  status={
                    action?.status === "complete"
                      ? "success"
                      : action?.status === "blocked"
                        ? "danger"
                        : "warning"
                  }
                >
                  {action?.status ?? "action open"}
                </StatusPill>
              </div>
              <h3>{rule.title}</h3>
              <small className="rule-instrument">{rule.instrument}</small>
              <div className="duty-holder">
                <BriefcaseBusiness size={16} />
                {dutyHolderLabels[rule.dutyHolder]} · {rule.jurisdiction}
              </div>
              <p>{rule.requirement}</p>
              <div className="expected-evidence">
                <span><FileSearch size={16} /> Expected evidence</span>
                <ul>{rule.expectedEvidence.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
              {action && (
                <div className="duty-action-summary">
                  <span><strong>Owner</strong>{action.owner || "Unassigned"}</span>
                  <span><strong>Due</strong>{action.dueDate || "Not set"}</span>
                  <span><strong>Evidence</strong>{action.evidenceIds.length}</span>
                </div>
              )}
              <div className="duty-card-actions">
                <a href={rule.sourceUrl} target="_blank" rel="noreferrer">
                  Exact official provision <ArrowUpRight size={14} />
                  <small>{rule.sourceLabel}</small>
                </a>
                <button className="button button-secondary" onClick={() => editAction(rule)}>
                  {action ? "Update action" : "Assign action"}
                </button>
              </div>
            </article>
          );
        })}
      </section>

      <section className="source-note">
        <Scale size={21} />
        <div><strong>{POLICY_PACK.title}</strong><p>Pack {POLICY_PACK.version}, effective {POLICY_PACK.effectiveDate}. {POLICY_PACK.notes}</p></div>
      </section>

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card modal-compact" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div><span className="eyebrow">{draft.rule.regulation}</span><h2>{draft.rule.title}</h2></div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close"><X size={21} /></button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <Field label="Action owner">
                  <input value={draft.action.owner} onChange={(e) => setDraft({ ...draft, action: { ...draft.action, owner: e.target.value } })} />
                </Field>
                <Field label="Due date">
                  <input type="date" value={draft.action.dueDate} onChange={(e) => setDraft({ ...draft, action: { ...draft.action, dueDate: e.target.value } })} />
                </Field>
                <Field label="Status">
                  <select value={draft.action.status} onChange={(e) => setDraft({ ...draft, action: { ...draft.action, status: e.target.value as ActionStatus } })}>
                    <option value="open">Open</option>
                    <option value="in-progress">In progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="complete">Complete</option>
                  </select>
                </Field>
                <Field label="Compliance response and decision" wide>
                  <textarea rows={4} value={draft.action.response} onChange={(e) => setDraft({ ...draft, action: { ...draft.action, response: e.target.value } })} />
                </Field>
                <Field label="Linked evidence" wide>
                  <div className="check-grid">
                    {evidence.map((item) => (
                      <label key={item.id}>
                        <input
                          type="checkbox"
                          checked={draft.action.evidenceIds.includes(item.id)}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              action: {
                                ...draft.action,
                                evidenceIds: e.target.checked
                                  ? [...draft.action.evidenceIds, item.id]
                                  : draft.action.evidenceIds.filter((id) => id !== item.id),
                              },
                            })
                          }
                        />
                        <span>{item.title || "Untitled evidence"}</span>
                      </label>
                    ))}
                  </div>
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              <div className="quality-summary">
                {draft.action.owner && draft.action.response ? <><CheckCircle2 size={17} /> Accountable response recorded</> : <><CircleAlert size={17} /> Owner and response are required</>}
              </div>
              <button className="button button-secondary" onClick={() => setDraft(null)}>Cancel</button>
              <button
                className="button button-primary"
                disabled={!draft.action.owner.trim() || !draft.action.response.trim()}
                onClick={() => {
                  onSaveAction({ ...draft.action, updatedAt: new Date().toISOString() });
                  setDraft(null);
                }}
              >
                Save duty action
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
